import React, { useState, useEffect } from 'react';
import { 
  Calculator, Triangle, Percent, Circle, ArrowLeftRight, 
  Box, Ruler, Scale, RotateCcw, Copy, Check, Weight, Plus, X, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { bulkMaterialDatabase } from '../data/materials';

const QuickCalculators = () => {
  // Pythagorean Calculator State
  const [pythA, setPythA] = useState('');
  const [pythB, setPythB] = useState('');
  const [pythC, setPythC] = useState('');
  const [pythSolveFor, setPythSolveFor] = useState('c');

  // Slope Calculator State
  const [slopeRise, setSlopeRise] = useState('');
  const [slopeRun, setSlopeRun] = useState('');
  const [slopeResult, setSlopeResult] = useState(null);

  // Circle Calculator State
  const [circleInput, setCircleInput] = useState('');
  const [circleInputType, setCircleInputType] = useState('radius');
  const [circleResult, setCircleResult] = useState(null);

  // Unit Converter State
  const [convertValue, setConvertValue] = useState('');
  const [convertFrom, setConvertFrom] = useState('feet');
  const [convertTo, setConvertTo] = useState('meters');
  const [convertResult, setConvertResult] = useState(null);

  // Concrete Calculator State
  const [concreteType, setConcreteType] = useState('slab');
  const [concreteLength, setConcreteLength] = useState('');
  const [concreteWidth, setConcreteWidth] = useState('');
  const [concreteDepth, setConcreteDepth] = useState('');
  const [concreteDiameter, setConcreteDiameter] = useState('');
  const [concreteHeight, setConcreteHeight] = useState('');
  const [concreteResult, setConcreteResult] = useState(null);

  // CY to Tons Converter State
  const [cyToTonsValue, setCyToTonsValue] = useState('');
  const [cyToTonsMaterial, setCyToTonsMaterial] = useState('');
  const [cyToTonsResult, setCyToTonsResult] = useState(null);
  const [customCyMaterials, setCustomCyMaterials] = useState(() => {
    const saved = localStorage.getItem('customCyToTonsMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAddCustomCyMaterial, setShowAddCustomCyMaterial] = useState(false);
  const [newCyMaterial, setNewCyMaterial] = useState({ name: '', tonsPerCubicYard: '' });

  // Save custom CY materials to localStorage
  useEffect(() => {
    localStorage.setItem('customCyToTonsMaterials', JSON.stringify(customCyMaterials));
  }, [customCyMaterials]);

  // Combined materials list for CY to Tons converter
  const allCyMaterials = [...bulkMaterialDatabase, ...customCyMaterials];

  // Copied state
  const [copied, setCopied] = useState(false);

  // Pythagorean Calculation
  const calculatePythagorean = () => {
    const a = parseFloat(pythA);
    const b = parseFloat(pythB);
    const c = parseFloat(pythC);

    let result;
    if (pythSolveFor === 'c' && a && b) {
      result = Math.sqrt(a * a + b * b);
      setPythC(result.toFixed(4));
      toast.success(`Hypotenuse (c) = ${result.toFixed(4)}`);
    } else if (pythSolveFor === 'a' && b && c) {
      result = Math.sqrt(c * c - b * b);
      setPythA(result.toFixed(4));
      toast.success(`Side (a) = ${result.toFixed(4)}`);
    } else if (pythSolveFor === 'b' && a && c) {
      result = Math.sqrt(c * c - a * a);
      setPythB(result.toFixed(4));
      toast.success(`Side (b) = ${result.toFixed(4)}`);
    } else {
      toast.error('Please enter two values to solve for the third');
    }
  };

  // Slope Calculation
  const calculateSlope = () => {
    const rise = parseFloat(slopeRise);
    const run = parseFloat(slopeRun);

    if (rise && run) {
      const percent = (rise / run) * 100;
      const ratio = `${rise}:${run}`;
      const angle = Math.atan(rise / run) * (180 / Math.PI);
      const pitchPer12 = (rise / run) * 12;

      setSlopeResult({
        percent: percent.toFixed(2),
        ratio,
        angle: angle.toFixed(2),
        pitchPer12: pitchPer12.toFixed(2)
      });
      toast.success('Slope calculated');
    } else {
      toast.error('Please enter rise and run values');
    }
  };

  // Circle Calculation
  const calculateCircle = () => {
    const value = parseFloat(circleInput);
    if (!value) {
      toast.error('Please enter a value');
      return;
    }

    let radius;
    if (circleInputType === 'radius') {
      radius = value;
    } else if (circleInputType === 'diameter') {
      radius = value / 2;
    } else if (circleInputType === 'circumference') {
      radius = value / (2 * Math.PI);
    }

    const diameter = radius * 2;
    const circumference = 2 * Math.PI * radius;
    const area = Math.PI * radius * radius;

    setCircleResult({
      radius: radius.toFixed(4),
      diameter: diameter.toFixed(4),
      circumference: circumference.toFixed(4),
      area: area.toFixed(4)
    });
    toast.success('Circle calculated');
  };

  // Unit Conversion
  const conversionRates = {
    // Length
    feet: { meters: 0.3048, inches: 12, yards: 0.333333, centimeters: 30.48 },
    meters: { feet: 3.28084, inches: 39.3701, yards: 1.09361, centimeters: 100 },
    inches: { feet: 0.0833333, meters: 0.0254, yards: 0.0277778, centimeters: 2.54 },
    yards: { feet: 3, meters: 0.9144, inches: 36, centimeters: 91.44 },
    centimeters: { feet: 0.0328084, meters: 0.01, inches: 0.393701, yards: 0.0109361 },
    // Weight
    pounds: { kilograms: 0.453592, tons: 0.0005, metricTons: 0.000453592 },
    kilograms: { pounds: 2.20462, tons: 0.00110231, metricTons: 0.001 },
    tons: { pounds: 2000, kilograms: 907.185, metricTons: 0.907185 },
    metricTons: { pounds: 2204.62, kilograms: 1000, tons: 1.10231 },
    // Volume
    cubicYards: { cubicFeet: 27, cubicMeters: 0.764555, gallons: 201.974 },
    cubicFeet: { cubicYards: 0.037037, cubicMeters: 0.0283168, gallons: 7.48052 },
    cubicMeters: { cubicYards: 1.30795, cubicFeet: 35.3147, gallons: 264.172 },
    gallons: { cubicYards: 0.00495113, cubicFeet: 0.133681, cubicMeters: 0.00378541 }
  };

  const calculateConversion = () => {
    const value = parseFloat(convertValue);
    if (!value) {
      toast.error('Please enter a value');
      return;
    }

    if (convertFrom === convertTo) {
      setConvertResult(value.toFixed(4));
      return;
    }

    const rate = conversionRates[convertFrom]?.[convertTo];
    if (rate) {
      setConvertResult((value * rate).toFixed(4));
      toast.success('Conversion complete');
    } else {
      toast.error('Invalid conversion pair');
    }
  };

  // Concrete Calculation
  const calculateConcrete = () => {
    let cubicFeet = 0;

    if (concreteType === 'slab' || concreteType === 'footing') {
      const l = parseFloat(concreteLength);
      const w = parseFloat(concreteWidth);
      const d = parseFloat(concreteDepth) / 12; // Convert inches to feet
      
      if (l && w && d) {
        cubicFeet = l * w * d;
      } else {
        toast.error('Please enter all dimensions');
        return;
      }
    } else if (concreteType === 'column') {
      const d = parseFloat(concreteDiameter);
      const h = parseFloat(concreteHeight);
      
      if (d && h) {
        const radius = d / 24; // Convert inches to feet, then divide by 2
        cubicFeet = Math.PI * radius * radius * h;
      } else {
        toast.error('Please enter diameter and height');
        return;
      }
    }

    const cubicYards = cubicFeet / 27;
    const bags60lb = cubicFeet * 2.2; // Approximate bags needed
    const bags80lb = cubicFeet * 1.65;

    setConcreteResult({
      cubicFeet: cubicFeet.toFixed(2),
      cubicYards: cubicYards.toFixed(3),
      bags60lb: Math.ceil(bags60lb),
      bags80lb: Math.ceil(bags80lb)
    });
    toast.success('Concrete volume calculated');
  };

  // CY to Tons Calculation
  const calculateCyToTons = () => {
    const cy = parseFloat(cyToTonsValue);
    if (!cy) {
      toast.error('Please enter cubic yards');
      return;
    }
    if (!cyToTonsMaterial) {
      toast.error('Please select a material');
      return;
    }

    const material = allCyMaterials.find(m => m.name === cyToTonsMaterial);
    if (!material) {
      toast.error('Material not found');
      return;
    }

    const tons = cy * material.tonsPerCubicYard;
    const pounds = tons * 2000;
    
    setCyToTonsResult({
      tons: tons.toFixed(3),
      pounds: pounds.toFixed(0),
      tonsPerCy: material.tonsPerCubicYard,
      materialName: material.name
    });
    toast.success('Conversion calculated');
  };

  // Add Custom CY Material
  const addCustomCyMaterial = () => {
    if (!newCyMaterial.name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    const tpy = parseFloat(newCyMaterial.tonsPerCubicYard);
    if (!tpy || tpy <= 0) {
      toast.error('Please enter a valid tons per cubic yard value');
      return;
    }

    const newMat = {
      name: newCyMaterial.name.trim(),
      category: 'Custom',
      tonsPerCubicYard: tpy,
      description: 'Custom material'
    };
    
    setCustomCyMaterials([...customCyMaterials, newMat]);
    setNewCyMaterial({ name: '', tonsPerCubicYard: '' });
    setShowAddCustomCyMaterial(false);
    toast.success(`Custom material "${newMat.name}" added`);
  };

  // Remove Custom CY Material
  const removeCustomCyMaterial = (name) => {
    setCustomCyMaterials(customCyMaterials.filter(m => m.name !== name));
    toast.success('Custom material removed');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const resetCalculator = (type) => {
    switch(type) {
      case 'pythagorean':
        setPythA('');
        setPythB('');
        setPythC('');
        break;
      case 'slope':
        setSlopeRise('');
        setSlopeRun('');
        setSlopeResult(null);
        break;
      case 'circle':
        setCircleInput('');
        setCircleResult(null);
        break;
      case 'convert':
        setConvertValue('');
        setConvertResult(null);
        break;
      case 'concrete':
        setConcreteLength('');
        setConcreteWidth('');
        setConcreteDepth('');
        setConcreteDiameter('');
        setConcreteHeight('');
        setConcreteResult(null);
        break;
      case 'cyToTons':
        setCyToTonsValue('');
        setCyToTonsMaterial('');
        setCyToTonsResult(null);
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Quick Calculators
          </h2>
          <p className="text-muted-foreground mt-1">
            Essential construction calculations at your fingertips
          </p>
        </div>
      </div>

      <Tabs defaultValue="pythagorean" className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="pythagorean" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Triangle className="w-4 h-4" />
            <span className="hidden sm:inline">Right Angle</span>
          </TabsTrigger>
          <TabsTrigger value="slope" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Percent className="w-4 h-4" />
            <span className="hidden sm:inline">Slope</span>
          </TabsTrigger>
          <TabsTrigger value="circle" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Circle className="w-4 h-4" />
            <span className="hidden sm:inline">Circle</span>
          </TabsTrigger>
          <TabsTrigger value="cyToTons" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Weight className="w-4 h-4" />
            <span className="hidden sm:inline">CY → Tons</span>
          </TabsTrigger>
          <TabsTrigger value="convert" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <ArrowLeftRight className="w-4 h-4" />
            <span className="hidden sm:inline">Convert</span>
          </TabsTrigger>
          <TabsTrigger value="concrete" className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Box className="w-4 h-4" />
            <span className="hidden sm:inline">Concrete</span>
          </TabsTrigger>
        </TabsList>

        {/* Pythagorean Calculator */}
        <TabsContent value="pythagorean">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Triangle className="w-5 h-5 text-primary" />
                Right Angle Calculator (a² + b² = c²)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pythA" className="flex items-center gap-2">
                    Side A {pythSolveFor === 'a' && <span className="text-xs text-primary">(solving)</span>}
                  </Label>
                  <Input
                    id="pythA"
                    type="number"
                    placeholder="Enter side A"
                    value={pythA}
                    onChange={(e) => setPythA(e.target.value)}
                    disabled={pythSolveFor === 'a'}
                    className="text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pythB" className="flex items-center gap-2">
                    Side B {pythSolveFor === 'b' && <span className="text-xs text-primary">(solving)</span>}
                  </Label>
                  <Input
                    id="pythB"
                    type="number"
                    placeholder="Enter side B"
                    value={pythB}
                    onChange={(e) => setPythB(e.target.value)}
                    disabled={pythSolveFor === 'b'}
                    className="text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pythC" className="flex items-center gap-2">
                    Hypotenuse C {pythSolveFor === 'c' && <span className="text-xs text-primary">(solving)</span>}
                  </Label>
                  <Input
                    id="pythC"
                    type="number"
                    placeholder="Enter hypotenuse C"
                    value={pythC}
                    onChange={(e) => setPythC(e.target.value)}
                    disabled={pythSolveFor === 'c'}
                    className="text-lg"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Solve for:</Label>
                <div className="flex gap-2">
                  {['a', 'b', 'c'].map((side) => (
                    <Button
                      key={side}
                      variant={pythSolveFor === side ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPythSolveFor(side)}
                    >
                      {side.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={calculatePythagorean} className="flex-1">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
                <Button variant="outline" onClick={() => resetCalculator('pythagorean')}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {/* Visual Triangle */}
              <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                <svg viewBox="0 0 200 150" className="w-full max-w-xs mx-auto">
                  <polygon points="20,130 180,130 180,30" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary" />
                  <text x="100" y="145" textAnchor="middle" className="fill-current text-xs">a = {pythA || '?'}</text>
                  <text x="190" y="80" textAnchor="start" className="fill-current text-xs">b = {pythB || '?'}</text>
                  <text x="90" y="70" textAnchor="middle" className="fill-current text-xs">c = {pythC || '?'}</text>
                  <rect x="170" y="120" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1" className="text-muted-foreground" />
                </svg>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Slope Calculator */}
        <TabsContent value="slope">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-primary" />
                Slope / Grade Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="slopeRise">Rise (vertical)</Label>
                  <Input
                    id="slopeRise"
                    type="number"
                    placeholder="Enter rise"
                    value={slopeRise}
                    onChange={(e) => setSlopeRise(e.target.value)}
                    className="text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slopeRun">Run (horizontal)</Label>
                  <Input
                    id="slopeRun"
                    type="number"
                    placeholder="Enter run"
                    value={slopeRun}
                    onChange={(e) => setSlopeRun(e.target.value)}
                    className="text-lg"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={calculateSlope} className="flex-1">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Slope
                </Button>
                <Button variant="outline" onClick={() => resetCalculator('slope')}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {slopeResult && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Percent Grade</p>
                    <p className="text-2xl font-bold text-primary">{slopeResult.percent}%</p>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Ratio</p>
                    <p className="text-2xl font-bold text-accent">{slopeResult.ratio}</p>
                  </div>
                  <div className="bg-success/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Angle</p>
                    <p className="text-2xl font-bold text-success">{slopeResult.angle}°</p>
                  </div>
                  <div className="bg-orange-500/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Pitch (per 12)</p>
                    <p className="text-2xl font-bold text-orange-500">{slopeResult.pitchPer12}/12</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Circle Calculator */}
        <TabsContent value="circle">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Circle className="w-5 h-5 text-primary" />
                Circle / Arc Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Input Type</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'radius', label: 'Radius' },
                      { value: 'diameter', label: 'Diameter' },
                      { value: 'circumference', label: 'Circumference' }
                    ].map((type) => (
                      <Button
                        key={type.value}
                        variant={circleInputType === type.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCircleInputType(type.value)}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="circleInput">{circleInputType.charAt(0).toUpperCase() + circleInputType.slice(1)}</Label>
                  <Input
                    id="circleInput"
                    type="number"
                    placeholder={`Enter ${circleInputType}`}
                    value={circleInput}
                    onChange={(e) => setCircleInput(e.target.value)}
                    className="text-lg"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={calculateCircle} className="flex-1">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
                <Button variant="outline" onClick={() => resetCalculator('circle')}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {circleResult && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Radius</p>
                    <p className="text-xl font-bold text-primary">{circleResult.radius}</p>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Diameter</p>
                    <p className="text-xl font-bold text-accent">{circleResult.diameter}</p>
                  </div>
                  <div className="bg-success/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Circumference</p>
                    <p className="text-xl font-bold text-success">{circleResult.circumference}</p>
                  </div>
                  <div className="bg-orange-500/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Area</p>
                    <p className="text-xl font-bold text-orange-500">{circleResult.area}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CY to Tons Converter */}
        <TabsContent value="cyToTons">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Weight className="w-5 h-5 text-primary" />
                Cubic Yards to Tons Converter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Convert cubic yards (CY) to tons based on material density. Select a material or add your own custom materials.
              </p>

              {/* Material Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cyToTonsValue">Cubic Yards (CY)</Label>
                  <Input
                    id="cyToTonsValue"
                    type="number"
                    placeholder="Enter cubic yards"
                    value={cyToTonsValue}
                    onChange={(e) => setCyToTonsValue(e.target.value)}
                    className="text-lg"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Material Type</Label>
                  <Select value={cyToTonsMaterial} onValueChange={setCyToTonsMaterial}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {/* Group materials by category */}
                      {Array.from(new Set(bulkMaterialDatabase.map(m => m.category))).map(category => (
                        <div key={category}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">{category}</div>
                          {bulkMaterialDatabase.filter(m => m.category === category).map(material => (
                            <SelectItem key={material.name} value={material.name}>
                              {material.name} ({material.tonsPerCubicYard} tons/cy)
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                      {customCyMaterials.length > 0 && (
                        <div>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">Custom Materials</div>
                          {customCyMaterials.map(material => (
                            <SelectItem key={material.name} value={material.name}>
                              {material.name} ({material.tonsPerCubicYard} tons/cy)
                            </SelectItem>
                          ))}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Calculate Button */}
              <div className="flex gap-2">
                <Button onClick={calculateCyToTons} className="flex-1">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Tons
                </Button>
                <Button variant="outline" onClick={() => resetCalculator('cyToTons')}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {/* Results */}
              {cyToTonsResult && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Tons</p>
                    <p className="text-2xl font-bold text-primary">{cyToTonsResult.tons}</p>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Pounds</p>
                    <p className="text-xl font-bold text-accent">{parseInt(cyToTonsResult.pounds).toLocaleString()}</p>
                  </div>
                  <div className="bg-success/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Conversion Rate</p>
                    <p className="text-xl font-bold text-success">{cyToTonsResult.tonsPerCy}</p>
                    <p className="text-xs text-muted-foreground">tons/cy</p>
                  </div>
                  <div className="bg-orange-500/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Material</p>
                    <p className="text-sm font-bold text-orange-500 truncate">{cyToTonsResult.materialName}</p>
                  </div>
                </div>
              )}

              {/* Custom Materials Management */}
              <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Custom Materials
                  </Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddCustomCyMaterial(!showAddCustomCyMaterial)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Custom
                  </Button>
                </div>

                {showAddCustomCyMaterial && (
                  <div className="grid grid-cols-3 gap-2 mb-3 p-3 bg-background rounded border">
                    <Input
                      placeholder="Material name"
                      value={newCyMaterial.name}
                      onChange={(e) => setNewCyMaterial({...newCyMaterial, name: e.target.value})}
                      className="h-9"
                    />
                    <Input
                      type="number"
                      placeholder="Tons per CY"
                      value={newCyMaterial.tonsPerCubicYard}
                      onChange={(e) => setNewCyMaterial({...newCyMaterial, tonsPerCubicYard: e.target.value})}
                      className="h-9"
                      step="0.1"
                      min="0"
                    />
                    <div className="flex gap-1">
                      <Button size="sm" onClick={addCustomCyMaterial} className="h-9 px-3">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddCustomCyMaterial(false)} className="h-9 px-3">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {customCyMaterials.length > 0 ? (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {customCyMaterials.map(material => (
                      <div key={material.name} className="flex items-center justify-between text-sm p-2 hover:bg-background rounded">
                        <span className="font-medium">{material.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{material.tonsPerCubicYard} tons/cy</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeCustomCyMaterial(material.name)}
                            className="h-6 w-6 p-0"
                          >
                            <X className="w-3 h-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No custom materials yet. Add your own materials with specific conversion factors.
                  </p>
                )}
              </div>

              {/* Quick Reference */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Soil & Dirt</p>
                  <p>Topsoil: 1.1-1.5 t/cy</p>
                  <p>Fill Dirt: 1.4-1.7 t/cy</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Sand</p>
                  <p>Dry: 1.3 t/cy</p>
                  <p>Wet: 1.7 t/cy</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Gravel & Stone</p>
                  <p>Crushed: 1.4-1.5 t/cy</p>
                  <p>River Rock: 1.5-1.6 t/cy</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Base</p>
                  <p>Road Base: 1.4 t/cy</p>
                  <p>Crushed Concrete: 1.4 t/cy</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Converter */}
        <TabsContent value="convert">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-primary" />
                Unit Converter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="convertValue">Value</Label>
                  <Input
                    id="convertValue"
                    type="number"
                    placeholder="Enter value"
                    value={convertValue}
                    onChange={(e) => setConvertValue(e.target.value)}
                    className="text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From</Label>
                  <select
                    value={convertFrom}
                    onChange={(e) => setConvertFrom(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <optgroup label="Length">
                      <option value="feet">Feet</option>
                      <option value="meters">Meters</option>
                      <option value="inches">Inches</option>
                      <option value="yards">Yards</option>
                      <option value="centimeters">Centimeters</option>
                    </optgroup>
                    <optgroup label="Weight">
                      <option value="pounds">Pounds (lbs)</option>
                      <option value="kilograms">Kilograms (kg)</option>
                      <option value="tons">Tons (US)</option>
                      <option value="metricTons">Metric Tons</option>
                    </optgroup>
                    <optgroup label="Volume">
                      <option value="cubicYards">Cubic Yards</option>
                      <option value="cubicFeet">Cubic Feet</option>
                      <option value="cubicMeters">Cubic Meters</option>
                      <option value="gallons">Gallons</option>
                    </optgroup>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <select
                    value={convertTo}
                    onChange={(e) => setConvertTo(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <optgroup label="Length">
                      <option value="feet">Feet</option>
                      <option value="meters">Meters</option>
                      <option value="inches">Inches</option>
                      <option value="yards">Yards</option>
                      <option value="centimeters">Centimeters</option>
                    </optgroup>
                    <optgroup label="Weight">
                      <option value="pounds">Pounds (lbs)</option>
                      <option value="kilograms">Kilograms (kg)</option>
                      <option value="tons">Tons (US)</option>
                      <option value="metricTons">Metric Tons</option>
                    </optgroup>
                    <optgroup label="Volume">
                      <option value="cubicYards">Cubic Yards</option>
                      <option value="cubicFeet">Cubic Feet</option>
                      <option value="cubicMeters">Cubic Meters</option>
                      <option value="gallons">Gallons</option>
                    </optgroup>
                  </select>
                </div>
                <Button onClick={calculateConversion}>
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Convert
                </Button>
              </div>

              {convertResult && (
                <div className="bg-primary/10 rounded-lg p-6 text-center mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Result</p>
                  <div className="flex items-center justify-center gap-4">
                    <p className="text-3xl font-bold text-primary">{convertResult}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(convertResult)}
                    >
                      {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {convertValue} {convertFrom} = {convertResult} {convertTo}
                  </p>
                </div>
              )}

              {/* Quick Conversion Reference */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Length</p>
                  <p>1 ft = 0.3048 m</p>
                  <p>1 yd = 3 ft</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Weight</p>
                  <p>1 ton = 2000 lbs</p>
                  <p>1 kg = 2.205 lbs</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Volume</p>
                  <p>1 cy = 27 cu ft</p>
                  <p>1 cy = 202 gal</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Area</p>
                  <p>1 sq ft = 144 sq in</p>
                  <p>1 sq yd = 9 sq ft</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Concrete Calculator */}
        <TabsContent value="concrete">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Box className="w-5 h-5 text-primary" />
                Concrete Volume Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 mb-4">
                {[
                  { value: 'slab', label: 'Slab', icon: '▭' },
                  { value: 'footing', label: 'Footing', icon: '▬' },
                  { value: 'column', label: 'Column', icon: '○' }
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={concreteType === type.value ? 'default' : 'outline'}
                    onClick={() => {
                      setConcreteType(type.value);
                      setConcreteResult(null);
                    }}
                    className="flex-1"
                  >
                    <span className="mr-2">{type.icon}</span>
                    {type.label}
                  </Button>
                ))}
              </div>

              {(concreteType === 'slab' || concreteType === 'footing') && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="concreteLength">Length (feet)</Label>
                    <Input
                      id="concreteLength"
                      type="number"
                      placeholder="Enter length"
                      value={concreteLength}
                      onChange={(e) => setConcreteLength(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="concreteWidth">Width (feet)</Label>
                    <Input
                      id="concreteWidth"
                      type="number"
                      placeholder="Enter width"
                      value={concreteWidth}
                      onChange={(e) => setConcreteWidth(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="concreteDepth">Depth/Thickness (inches)</Label>
                    <Input
                      id="concreteDepth"
                      type="number"
                      placeholder="Enter depth"
                      value={concreteDepth}
                      onChange={(e) => setConcreteDepth(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                </div>
              )}

              {concreteType === 'column' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="concreteDiameter">Diameter (inches)</Label>
                    <Input
                      id="concreteDiameter"
                      type="number"
                      placeholder="Enter diameter"
                      value={concreteDiameter}
                      onChange={(e) => setConcreteDiameter(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="concreteHeight">Height (feet)</Label>
                    <Input
                      id="concreteHeight"
                      type="number"
                      placeholder="Enter height"
                      value={concreteHeight}
                      onChange={(e) => setConcreteHeight(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={calculateConcrete} className="flex-1">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Volume
                </Button>
                <Button variant="outline" onClick={() => resetCalculator('concrete')}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              {concreteResult && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Cubic Feet</p>
                    <p className="text-2xl font-bold text-primary">{concreteResult.cubicFeet}</p>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Cubic Yards</p>
                    <p className="text-2xl font-bold text-accent">{concreteResult.cubicYards}</p>
                  </div>
                  <div className="bg-success/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">60 lb Bags</p>
                    <p className="text-2xl font-bold text-success">{concreteResult.bags60lb}</p>
                  </div>
                  <div className="bg-orange-500/10 rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">80 lb Bags</p>
                    <p className="text-2xl font-bold text-orange-500">{concreteResult.bags80lb}</p>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-2">
                * Add 10-15% extra for waste and spillage. Bag estimates are approximate.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuickCalculators;
